import { useState, useEffect, useRef } from 'react';
import Sidebar from '../home/components/Sidebar';
import Navbar from '../home/components/Navbar';
import { getCurrentUser } from '../../services/auth';
import { deductCredits } from '../../services/credits';
import { createHistory, updateHistoryByTaskId } from '../../services/history';
import {
  getVideoModelConfig,
  createVideoTask,
  getVideoTaskStatus,
  getVideoUrlFromTaskResult,
  isArkVideoGenerationModel,
  type VideoModelConfig,
} from '../../services/video';
import { getTextModelConfig, generateStoryboardScenesFromText } from '../../services/storyboard';

const STORYBOARD_CREDITS_PER_USE = 10;

export default function Sora2StoryboardPage() {
  const [activeMenu, setActiveMenu] = useState('故事板');
  const [scenes, setScenes] = useState([{ id: 1, type: '镜头描述' as const, prompt: '' }]);
  const [aiStoryboardGenerated, setAiStoryboardGenerated] = useState(false);
  const [selectedRatio, setSelectedRatio] = useState('9:16');
  const [selectedDuration, setSelectedDuration] = useState('10秒');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [storyboardInput, setStoryboardInput] = useState('');
  const [isGeneratingShots, setIsGeneratingShots] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<Awaited<ReturnType<typeof getCurrentUser>>>(null);
  const [videoModelConfig, setVideoModelConfig] = useState<VideoModelConfig | null>(null);
  const [videoTaskId, setVideoTaskId] = useState<string | null>(null);
  const [taskStatus, setTaskStatus] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [generatedVideoUrl, setGeneratedVideoUrl] = useState<string | null>(null);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const load = async () => {
      const [u, config] = await Promise.all([getCurrentUser(), getVideoModelConfig()]);
      setUser(u);
      setVideoModelConfig(config ?? null);
    };
    load();
  }, []);

  const durations = ['5秒', '10秒'];
  const storyboardCases = [
    {
      name: '公园与会议',
      text: '白天公园里小朋友在玩耍。会议室里几位同事正在讨论方案。最后大家达成一致，鼓掌庆祝。',
    },
    {
      name: '咖啡馆场景',
      text: '咖啡馆内，一位年轻人坐在窗边，手捧咖啡杯凝视窗外。镜头从侧面特写他的表情，窗外是繁忙的街道。',
    },
    {
      name: '产品展示',
      text: '电影感全景镜头，阳光柔和的场景，用户惬意地注视着产品。中景跟拍，用户开始使用产品，脸上浮现愉悦笑容。产品特写静态展示，细节特写，质感卓越。',
    },
  ];
  const ratios = [
    { label: '竖屏 9:16', value: '9:16' },
    { label: '横屏 16:9', value: '16:9' }
  ];

  const handleAddScene = () => {
    const newScene = {
      id: scenes.length + 1,
      type: '镜头描述',
      prompt: ''
    };
    setScenes([...scenes, newScene]);
  };

  const handleRemoveScene = (id: number) => {
    if (scenes.length > 1) {
      setScenes(scenes.filter(scene => scene.id !== id));
    }
  };

  const handleSceneChange = (id: number, field: 'prompt', value: string) => {
    setScenes(scenes.map(scene => 
      scene.id === id ? { ...scene, [field]: value } : scene
    ));
  };

  const handleGenerateShots = async () => {
    if (!storyboardInput.trim()) {
      setError('请输入故事或脚本内容');
      return;
    }
    setError(null);
    setIsGeneratingShots(true);
    try {
      const textConfig = await getTextModelConfig();
      const apiScenes = await generateStoryboardScenesFromText(storyboardInput.trim(), textConfig);
      if (apiScenes.length === 0) {
        setError('未能解析出分镜，请换一种描述方式或手动添加镜头');
        return;
      }
      const newScenes = apiScenes.map((s, i) => ({
        id: i + 1,
        type: '镜头描述' as const,
        prompt: s.prompt,
      }));
      setScenes(newScenes);
      setAiStoryboardGenerated(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'AI 生成分镜失败');
    } finally {
      setIsGeneratingShots(false);
    }
  };

  const checkTaskStatus = async (taskId: string) => {
    if (!videoModelConfig?.api_key) return null;
    try {
      const taskData = await getVideoTaskStatus(videoModelConfig, taskId);
      if (!taskData) return null;
      setTaskStatus(taskData.status ?? null);
      switch (taskData.status) {
        case 'queued':
          setProgress(20);
          break;
        case 'running':
          setProgress((p) => Math.min(p + 10, 90));
          break;
        case 'succeeded': {
          setProgress(100);
          const videoUrl = getVideoUrlFromTaskResult(taskData);
          if (videoUrl) setGeneratedVideoUrl(videoUrl);
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          if (user && videoUrl) {
            await updateHistoryByTaskId(user.id, taskId, { status: 'succeeded', output_url: videoUrl });
          }
          break;
        }
        case 'failed':
        case 'expired':
          setProgress(0);
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          if (user) {
            await updateHistoryByTaskId(user.id, taskId, { status: taskData.status });
          }
          break;
        default:
          break;
      }
      return taskData;
    } catch (e) {
      console.error('查询任务状态失败', e);
      return null;
    }
  };

  const startPolling = () => {
    if (!videoTaskId) return;
    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    checkTaskStatus(videoTaskId);
    pollingIntervalRef.current = setInterval(() => checkTaskStatus(videoTaskId), 3000);
  };

  useEffect(() => () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const handleGenerate = async () => {
    setError(null);
    if (!aiStoryboardGenerated) {
      setError('请先使用「AI 生成分镜」生成镜头列表，再生成视频');
      return;
    }
    const validScenes = scenes.filter((s) => s.prompt.trim());
    if (validScenes.length === 0) {
      setError('镜头列表为空，请重新使用 AI 生成分镜');
      return;
    }
    if (!user) {
      setError('请先登录');
      return;
    }
    if (!videoModelConfig?.model_name) {
      setError('视频模型未配置或未启用，请在后台配置并启用视频模型');
      return;
    }
    if (videoModelConfig.provider !== 'alibaba' && !videoModelConfig.api_key) {
      setError('当前视频模型缺少可用密钥。请启用阿里视频模型，或重新配置火山引擎/钱多多密钥');
      return;
    }
    if (!isArkVideoGenerationModel(videoModelConfig.model_name)) {
      setError('当前视频模型配置不正确，请在后台启用 Seedance 视频模型（如 doubao-seedance-1-5-pro）');
      return;
    }

    setIsGenerating(true);
    try {
      const deductResult = await deductCredits(
        user.id,
        STORYBOARD_CREDITS_PER_USE,
        '使用故事板生成视频'
      );
      if (!deductResult.success) {
        setError(deductResult.error || '积分不足，请前往充值');
        setIsGenerating(false);
        return;
      }

      // 火山方舟视频 API 仅支持 5、10 秒
      const durationNum = parseInt(selectedDuration.replace(/秒/g, ''), 10) || 10;
      const apiDuration = [5, 10].includes(durationNum) ? durationNum : 10;
      const ratioValue = selectedRatio === '16:9' ? '16:9' : '9:16';
      // 按页面「视频时长」均分到各镜头，生成分镜描述
      const n = validScenes.length;
      const perScene = Math.floor(apiDuration / n);
      const remainder = apiDuration - perScene * n;
      let startSec = 0;
      const combinedPrompt = validScenes
        .map((s, i) => {
          const dur = perScene + (i < remainder ? 1 : 0);
          const endSec = startSec + dur;
          const part = `分镜${i + 1}(${startSec}-${endSec}秒): ${s.prompt.trim()}`;
          startSec = endSec;
          return part;
        })
        .join('。');

      const taskResult = await createVideoTask(videoModelConfig, {
        prompt: combinedPrompt,
        duration: apiDuration,
        ratio: ratioValue,
        imageUrl: selectedImage || undefined,
        camera_fixed: false,
      });

      if (taskResult?.id) {
        await createHistory(user.id, {
          type: 'video',
          category: 'sora2-video',
          status: 'pending',
          task_id: taskResult.id,
          prompt: combinedPrompt,
          settings: { duration: apiDuration, ratio: ratioValue, storyboard: true },
          credits_used: STORYBOARD_CREDITS_PER_USE,
        });
        setVideoTaskId(taskResult.id);
        setProgress(0);
        setTaskStatus('queued');
        setGeneratedVideoUrl(null);
        setShowSuccessModal(true);
      } else {
        setError('视频任务创建失败，请稍后重试');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : typeof err === 'string' ? err : '视频生成失败';
      setError(msg);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSuccessConfirm = () => {
    setShowSuccessModal(false);
    startPolling();
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex">
      <Sidebar activeMenu={activeMenu} onMenuChange={setActiveMenu} />
      
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />

        <main className="flex-1 main-content-padding overflow-y-auto overflow-x-hidden">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-3xl font-bold mb-2">故事板视频</h1>
            <p className="text-gray-400 text-sm mb-8">
              演示故事板能够生成多个镜头，可选上传参考图，设置尺寸等可选参数。<br />
              支持使用故事板模式以及单一一次性生成。
            </p>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6 flex items-center gap-2">
                <i className="ri-error-warning-line text-red-400 shrink-0"></i>
                <p className="text-red-400 text-sm">{error}</p>
                <button type="button" onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-white">
                  <i className="ri-close-line"></i>
                </button>
              </div>
            )}

            {videoTaskId && (
              <div className="bg-[#1a1a1a] border border-gray-800 rounded-2xl p-6 mb-6">
                <h3 className="text-lg font-semibold mb-4">视频生成进度</h3>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm text-gray-400">
                    <span>状态：{taskStatus === 'succeeded' ? '已完成' : taskStatus === 'failed' || taskStatus === 'expired' ? '失败/已过期' : '处理中'}</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="h-2 bg-[#0a0a0a] rounded-full overflow-hidden">
                    <div className="h-full bg-blue-600 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
                  </div>
                </div>
                {generatedVideoUrl && (
                  <div className="mt-4">
                    <p className="text-sm text-gray-400 mb-2">生成结果：</p>
                    <video src={generatedVideoUrl} controls className="max-w-full rounded-lg max-h-80" />
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
              {/* Left Column - Upload and Generate */}
              <div className="lg:col-span-2 space-y-6">
                {/* Upload Section */}
                <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800">
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-image-line text-blue-400 text-lg"></i>
                    <h3 className="text-lg font-semibold">参考图（可选）</h3>
                  </div>
                  
                  <div className="border-2 border-dashed border-gray-700 rounded-xl p-12 text-center hover:border-blue-500/50 transition-colors cursor-pointer relative">
                    {selectedImage ? (
                      <div className="relative">
                        <img src={selectedImage} alt="Preview" className="max-h-64 mx-auto rounded-lg" />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedImage(null);
                          }}
                          className="absolute top-2 right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors cursor-pointer"
                        >
                          <i className="ri-close-line text-white"></i>
                        </button>
                      </div>
                    ) : (
                      <>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                          <i className="ri-upload-cloud-line text-blue-400 text-3xl"></i>
                        </div>
                        <p className="text-gray-400 mb-2">上传参考图片</p>
                        <p className="text-gray-500 text-xs">拖拽图片到此处或点击上传，支持 JPG、PNG 格式</p>
                      </>
                    )}
                  </div>
                </div>

                {/* Video Ratio */}
                <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800">
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-aspect-ratio-line text-cyan-400 text-lg"></i>
                    <h3 className="text-lg font-semibold">视频尺寸</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {ratios.map((ratio) => (
                      <button
                        key={ratio.value}
                        onClick={() => setSelectedRatio(ratio.value)}
                        className={`py-3 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                          selectedRatio === ratio.value
                            ? 'bg-blue-600 text-white'
                            : 'bg-[#0a0a0a] text-gray-300 border border-gray-700 hover:border-blue-500'
                        }`}
                      >
                        {ratio.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Duration */}
                <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800">
                  <div className="flex items-center gap-2 mb-4">
                    <i className="ri-time-line text-green-400 text-lg"></i>
                    <h3 className="text-lg font-semibold">视频时长</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3 w-full">
                    {durations.map((duration) => (
                      <button
                        key={duration}
                        onClick={() => setSelectedDuration(duration)}
                        className={`w-full py-3 rounded-lg text-sm font-medium transition-all cursor-pointer ${
                          selectedDuration === duration
                            ? 'bg-blue-600 text-white'
                            : 'bg-[#0a0a0a] text-gray-300 border border-gray-700 hover:border-blue-500'
                        }`}
                      >
                        {duration}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Generate Button */}
                <button 
                  onClick={handleGenerate}
                  disabled={isGenerating || !aiStoryboardGenerated}
                  className={`w-full py-4 font-semibold rounded-xl transition-all shadow-lg cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
                    isGenerating || !aiStoryboardGenerated
                      ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-blue-500/20 hover:shadow-blue-500/40'
                  }`}
                >
                  {isGenerating ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-xl"></i>
                      <span>生成中...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-play-circle-line text-xl"></i>
                      <span>立即生成</span>
                      <span className="bg-white/20 px-2 py-0.5 rounded text-xs">{STORYBOARD_CREDITS_PER_USE} 点</span>
                    </>
                  )}
                </button>
              </div>

              {/* Right Column - Scenes List */}
              <div className="lg:col-span-3 space-y-6">
                {/* AI 生成分镜 */}
                <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800">
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                      <i className="ri-magic-line text-amber-400 text-lg"></i>
                      <h3 className="text-lg font-semibold">AI 生成分镜</h3>
                    </div>
                    <div className="relative group">
                      <button
                        type="button"
                        onClick={() => setStoryboardInput(storyboardCases[0].text)}
                        className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 text-xs rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                      >
                        <i className="ri-file-list-3-line"></i>
                        <span>插入案例</span>
                        <i className="ri-arrow-down-s-line text-sm"></i>
                      </button>
                      <div className="absolute right-0 top-full mt-1 py-1 bg-[#0a0a0a] border border-gray-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 min-w-[140px]">
                        {storyboardCases.map((c) => (
                          <button
                            key={c.name}
                            type="button"
                            onClick={() => setStoryboardInput(c.text)}
                            className="w-full px-3 py-2 text-left text-xs text-gray-300 hover:bg-gray-700 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                          >
                            {c.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mb-3">
                    输入故事或脚本，将使用文本模型进行分镜扩充生成，并填入下方镜头列表。
                  </p>
                  <textarea
                    value={storyboardInput}
                    onChange={(e) => setStoryboardInput(e.target.value)}
                    placeholder="例如：白天公园里小朋友在玩耍。会议室里几位同事正在讨论方案。最后大家达成一致，鼓掌庆祝。"
                    className="w-full bg-[#0a0a0a] border border-gray-700 rounded-xl p-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors resize-none mb-3"
                    rows={4}
                  />
                  <button
                    type="button"
                    onClick={handleGenerateShots}
                    disabled={isGeneratingShots || !storyboardInput.trim()}
                    className={`w-full py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer flex items-center justify-center gap-2 ${
                      isGeneratingShots || !storyboardInput.trim()
                        ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                        : 'bg-amber-600 hover:bg-amber-700 text-white'
                    }`}
                  >
                    {isGeneratingShots ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        <span>生成中...</span>
                      </>
                    ) : (
                      <>
                        <i className="ri-magic-line"></i>
                        <span>AI 生成分镜</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Scenes List */}
                <div className="bg-[#1a1a1a] rounded-2xl p-6 border border-gray-800">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <i className="ri-film-line text-purple-400 text-lg"></i>
                      <h3 className="text-lg font-semibold">镜头列表</h3>
                    </div>
                    <button 
                      onClick={handleAddScene}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                    >
                      <i className="ri-add-line"></i>
                      <span>添加</span>
                    </button>
                  </div>

                  <p className="text-xs text-gray-400 mb-4">
                    用于多个不同镜头，可输入生成镜头与镜头描述，可以批量生成对比。
                  </p>

                  <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
                    {scenes.map((scene, index) => (
                      <div key={scene.id} className="bg-[#0a0a0a] rounded-xl p-3 border border-gray-800">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-8 h-8 bg-blue-600/10 rounded-lg flex items-center justify-center">
                            <span className="text-blue-400 font-semibold text-sm">{index + 1}</span>
                          </div>

                          <div className="flex-1 space-y-2">
                            {scenes.length > 1 && (
                              <div className="flex items-center justify-end">
                                <button
                                  onClick={() => handleRemoveScene(scene.id)}
                                  className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-red-400 transition-colors cursor-pointer"
                                >
                                  <i className="ri-delete-bin-line text-sm"></i>
                                </button>
                              </div>
                            )}

                            <textarea
                              value={scene.prompt}
                              onChange={(e) => handleSceneChange(scene.id, 'prompt', e.target.value)}
                              placeholder="描述这个镜头的内容..."
                              className="w-full bg-[#1a1a1a] border border-gray-700 rounded-lg p-2 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors resize-none"
                              rows={3}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tips */}
                <div className="bg-gradient-to-br from-blue-600/10 to-purple-600/10 rounded-2xl p-6 border border-blue-500/20">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-lightbulb-line text-blue-400"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-blue-400 mb-2">使用提示</h4>
                      <ul className="text-xs text-gray-400 space-y-1">
                        <li>• 须先完成「AI 生成分镜」后再点击「立即生成」</li>
                        <li>• 视频总时长由页面设置，将均分到各镜头</li>
                        <li>• 镜头描述越详细，生成效果越好</li>
                        <li>• 可以添加多个镜头组成完整故事</li>
                        <li>• 支持上传参考图提升生成质量</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
          <div className="bg-[#1a1a1a] rounded-2xl p-8 max-w-md w-full border border-gray-800 relative">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center mb-4">
                <i className="ri-check-line text-white text-3xl"></i>
              </div>
              
              <h2 className="text-2xl font-bold mb-3">提交成功</h2>
              
              <p className="text-gray-400 text-sm mb-6 leading-relaxed">
                任务已提交成功，约5分钟后可在素材空间或历史记录中查看生成结果。
              </p>

              <button
                onClick={handleSuccessConfirm}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all shadow-lg shadow-blue-500/20 hover:shadow-blue-500/40 cursor-pointer whitespace-nowrap"
              >
                确定
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type="number"] {
          -moz-appearance: textfield;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #0a0a0a;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #374151;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #4b5563;
        }
      `}</style>
    </div>
  );
}
